// 위치 서비스 관련 유틸리티 함수들

// 현재 위치 정보 타입
export interface LocationData {
  latitude: number;
  longitude: number;
  address?: string;
}

// 주소 정보 타입
export interface AddressData {
  address: string;
  roadAddress?: string;
  jibunAddress?: string;
}

// 현재 위치 가져오기 (Geolocation API 사용)
export const getCurrentPosition = (): Promise<LocationData> => {
  return new Promise((resolve, reject) => {
    // 브라우저에서 Geolocation API 지원 확인
    if (!navigator.geolocation) {
      reject(new Error('이 브라우저에서는 위치 서비스를 지원하지 않습니다.'));
      return;
    }

    // 위치 정보 옵션
    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000 // 5분
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        resolve({
          latitude,
          longitude
        });
      },
      (error) => {
        let errorMessage = '위치 정보를 가져올 수 없습니다.';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = '위치 접근 권한이 거부되었습니다. 브라우저 설정에서 위치 권한을 허용해주세요.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = '위치 정보를 사용할 수 없습니다.';
            break;
          case error.TIMEOUT:
            errorMessage = '위치 정보 요청이 시간 초과되었습니다.';
            break;
          default:
            errorMessage = '알 수 없는 오류가 발생했습니다.';
            break;
        }
        
        reject(new Error(errorMessage));
      },
      options
    );
  });
};

// 카카오맵 API를 사용한 역지오코딩 (위도/경도 → 주소)
export const getAddressFromCoordinates = async (latitude: number, longitude: number): Promise<AddressData> => {
  try {
    console.log('주소 변환 시작:', { latitude, longitude });
    
    // 카카오맵 API 로드 대기
    const { waitForKakaoMap } = await import('./kakaoLoader');
    await waitForKakaoMap();
    
    console.log('카카오맵 API 로드 완료');
    
    // 카카오맵 API 상태 상세 확인
    const apiStatus = {
      windowKakao: !!window.kakao,
      kakaoMaps: !!(window.kakao && window.kakao.maps),
      kakaoServices: !!(window.kakao && window.kakao.maps && window.kakao.maps.services),
      kakaoGeocoder: !!(window.kakao && window.kakao.maps && window.kakao.maps.services && window.kakao.maps.services.Geocoder),
      kakaoStatus: !!(window.kakao && window.kakao.maps && window.kakao.maps.services && window.kakao.maps.services.Status)
    };
    
    console.log('카카오맵 API 상태:', apiStatus);
    
    if (!apiStatus.windowKakao) {
      throw new Error('window.kakao 객체가 없습니다.');
    }
    if (!apiStatus.kakaoMaps) {
      throw new Error('kakao.maps 객체가 없습니다.');
    }
    if (!apiStatus.kakaoServices) {
      throw new Error('kakao.maps.services 객체가 없습니다.');
    }
    if (!apiStatus.kakaoGeocoder) {
      throw new Error('kakao.maps.services.Geocoder 클래스가 없습니다.');
    }

    const geocoder = new window.kakao.maps.services.Geocoder();
    console.log('Geocoder 인스턴스 생성 완료');

    return new Promise((resolve, reject) => {
      geocoder.coord2Address(longitude, latitude, (result: any, status: any) => {
        console.log('주소 변환 결과:', { result, status });
        
        if (status === window.kakao.maps.services.Status.OK) {
          const addressInfo = result[0];
          const addressData = {
            address: addressInfo.address?.address_name || '주소를 찾을 수 없습니다.',
            roadAddress: addressInfo.road_address?.address_name,
            jibunAddress: addressInfo.address?.address_name
          };
          console.log('주소 변환 성공:', addressData);
          resolve(addressData);
        } else {
          console.error('주소 변환 실패 - 상태:', status);
          reject(new Error(`주소 변환에 실패했습니다. 상태: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error('주소 변환 중 에러 발생:', error);
    throw new Error(`주소 변환 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
  }
};

// 현재 위치와 주소를 함께 가져오기
export const getCurrentLocationWithAddress = async (): Promise<LocationData> => {
  try {
    // 1. 현재 위치 가져오기
    const location = await getCurrentPosition();
    
    // 2. 주소 변환 시도
    try {
      const addressData = await getAddressFromCoordinates(location.latitude, location.longitude);
      return {
        ...location,
        address: addressData.roadAddress || addressData.address
      };
    } catch (addressError) {
      // 주소 변환 실패 시 좌표만 반환
      console.warn('주소 변환 실패:', addressError);
      return {
        ...location,
        address: `위도: ${location.latitude.toFixed(6)}, 경도: ${location.longitude.toFixed(6)}`
      };
    }
  } catch (error) {
    throw error;
  }
};

// 좌표를 주소 문자열로 포맷팅
export const formatLocationDisplay = (latitude: number, longitude: number, address?: string): string => {
  if (address) {
    return address;
  }
  return `위도: ${latitude.toFixed(6)}, 경도: ${longitude.toFixed(6)}`;
};

// 주소를 좌표로 변환 (지오코딩)
export const getCoordinatesFromAddress = async (address: string): Promise<LocationData> => {
  try {
    console.log('좌표 변환 시작:', address);
    
    // 카카오맵 API 로드 대기
    const { waitForKakaoMap } = await import('./kakaoLoader');
    await waitForKakaoMap();
    
    console.log('카카오맵 API 로드 완료');
    
    // 카카오맵 API 상태 확인
    if (!window.kakao || !window.kakao.maps || !window.kakao.maps.services) {
      throw new Error('카카오맵 API가 로드되지 않았습니다.');
    }

    const geocoder = new window.kakao.maps.services.Geocoder();
    const places = new window.kakao.maps.services.Places();
    console.log('Geocoder 및 Places 인스턴스 생성 완료');

    // 먼저 주소 검색 시도
    return new Promise((resolve, reject) => {
      geocoder.addressSearch(address, (result: any, status: any) => {
        console.log('주소 검색 결과:', { result, status, resultLength: result?.length });
        
        if (status === window.kakao.maps.services.Status.OK && result && result.length > 0) {
          const coords = result[0];
          const locationData: LocationData = {
            latitude: parseFloat(coords.y),
            longitude: parseFloat(coords.x),
            address: coords.road_address?.address_name || coords.address?.address_name || coords.address_name || address
          };
          console.log('주소 검색 성공:', locationData);
          resolve(locationData);
        } else {
          // 주소 검색 실패 시 키워드 검색으로 시도
          console.log('주소 검색 실패, 키워드 검색 시도');
          places.keywordSearch(address, (keywordResult: any, keywordStatus: any) => {
            console.log('키워드 검색 결과:', { keywordResult, keywordStatus, resultLength: keywordResult?.length });
            
            if (keywordStatus === window.kakao.maps.services.Status.OK && keywordResult && keywordResult.length > 0) {
              const place = keywordResult[0];
              const locationData: LocationData = {
                latitude: parseFloat(place.y),
                longitude: parseFloat(place.x),
                address: place.road_address_name || place.address_name || address
              };
              console.log('키워드 검색 성공:', locationData);
              resolve(locationData);
            } else {
              console.error('키워드 검색도 실패');
              reject(new Error('검색 결과가 없습니다. 더 구체적인 주소나 장소명을 입력해주세요.\n\n예시:\n- 서울특별시 강남구 테헤란로 123\n- 강남역\n- 서울시청'));
            }
          });
        }
      });
    });
  } catch (error) {
    console.error('주소 검색 중 에러 발생:', error);
    throw new Error(`주소 검색 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
  }
};